import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { loginOAuthRoutine, resetAuthErrorRoutine } from 'redux-state/ducks/auth';
import { addDeviceAuthParams, getDeviceAuthParams, isDeviceLimitEnabled } from 'utils/device';
import logger from 'utils/logger';
import ROUTES from 'utils/routes';
import { AUTH_ERROR_MESSAGES, GENERIC_AUTH_ERROR_MESSAGE } from './use-oauth-popup.constants';

const createPopup = ({ authWebUrl, appName, width, height }) => {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2.5;
  const externalPopup = window.open(authWebUrl, appName, `width=${width},height=${height},left=${left},top=${top}`);

  return externalPopup;
};

const getErrorMessage = (error) =>
  Object.prototype.hasOwnProperty.call(AUTH_ERROR_MESSAGES, error)
    ? AUTH_ERROR_MESSAGES[error]
    : GENERIC_AUTH_ERROR_MESSAGE;

const useOauthPopup = ({ width = 600, height = 650, onClose, redirectTo } = {}) => {
  const dispatch = useDispatch();
  const appConfig = useSelector((state) => state?.app?.config);
  const { appName, authWebUrl } = appConfig;
  const deviceLimitEnabled = isDeviceLimitEnabled(appConfig);

  const [externalWindow, setExternalWindow] = useState();

  const handleClick = useCallback(() => {
    dispatch(resetAuthErrorRoutine.request());

    if (!deviceLimitEnabled) {
      setExternalWindow(
        createPopup({
          width,
          height,
          appName,
          authWebUrl,
        }),
      );

      return;
    }

    // Open synchronously within the click so it isn't popup-blocked, then navigate once the device id
    // (which may need IndexedDB) is known.
    const popup = createPopup({
      width,
      height,
      appName,
      authWebUrl: '',
    });

    setExternalWindow(popup);

    if (!popup) {
      return;
    }

    getDeviceAuthParams()
      .then((params) => addDeviceAuthParams(authWebUrl, params))
      .catch((err) => {
        logger.error('useOauthPopup', 'Could not add device params to the auth URL', err?.message);
        return authWebUrl;
      })
      .then((url) => {
        if (!popup.closed) {
          popup.location.href = url;
        }
      });
  }, [dispatch, authWebUrl, appName, width, height, deviceLimitEnabled]);

  const handleMessageEvent = useCallback(
    (event) => {
      if (event.origin === new URL(authWebUrl).origin) {
        const accessToken = event?.data?.token;
        const error = event?.data?.error;

        if (accessToken) {
          externalWindow?.close?.();
          dispatch(loginOAuthRoutine.request({ token: accessToken, redirectTo }));
          onClose?.();
        } else if (error) {
          // the login was refused: never leave the popup open waiting for a token that won't come
          externalWindow?.close?.();

          const message = getErrorMessage(error);
          if (message) {
            dispatch(loginOAuthRoutine.failure(message));

            // only the landing page shows the error, so take header logins there
            if (window.location.pathname !== ROUTES.root) {
              dispatch(push(redirectTo ? `${ROUTES.root}?next=${redirectTo}` : ROUTES.root));
            }
          }

          onClose?.();
        }
      }
    },
    [externalWindow],
  );

  useEffect(() => {
    if (externalWindow) {
      window.externalWindow = externalWindow;

      window.addEventListener('message', handleMessageEvent);
    }

    return () => {
      if (externalWindow) {
        externalWindow.close();
        window.removeEventListener('message', handleMessageEvent);
      }
    };
  }, [externalWindow]);

  return handleClick;
};

export default useOauthPopup;
